import { Notice, SquareLoader } from "components/common-ui";
import Pager from "components/pager";
import { dateWrapper, SelfServiceContext } from "core";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import Categories from "./categories";
import Search from "./search";
import { useEffectOnce } from "@menu/core/hooks";
import useInboxLogic from "./use-inbox-logic";
import getAnalyticTags, { AnalyticsMetaData } from "@lib-shared/lib/get-analytics-tags";
import { Message } from "../../core/services/inbox.service";

const getMessageDate = (date: any) => {
    const dw = dateWrapper(date);

    if (dw.isTodaysDate) {
        return dw.getTimeString(false);
    }

    return dw.dayShortMonthAndFullYear;
}

const getAnalytics = (message: Message) => {
    return getAnalyticTags({
        component_name: "my_messages",
        component_type: "links",
        section_name: message.category.title,
        cta_text: `${message.category.title}_${message.content.title}`,
        target_page_url: location.href
    });
}

const getDeleteAnalytics = (message: Message) => {
    return getAnalyticTags({
        component_name: "my_messages",
        component_type: "links",
        section_name: message.category.title,
        cta_text: `Delete ${message.category.title}_${message.content.title}`,
        target_page_url: location.href
    });
}

const pushPaginationAnalytics = (pageNumber: number) => {

    const dl = (window as any)?.dataLayer ?? [];

    var analytics: AnalyticsMetaData = {
        component_name: "pagination",
        component_type: "links",
        section_name: `my_messages_${pageNumber}`,
        cta_text: `${pageNumber}`,
        target_page_url: location.href
    }

    dl.push(analytics);
}

const Messages = observer(() => {
    const selfServiceContext = useContext(SelfServiceContext);
    const store = selfServiceContext.stores.inbox;
    const { deleteMessageAsync, viewMessage } = useInboxLogic();

    useEffectOnce(() => {
        const load = async () => {
            await selfServiceContext.managers.inbox.populateAsync();
            store.updateCategory(store.category);
        }

        load();
    });

    const onDelete = () => selfServiceContext.managers.inbox.populateAsync();

    if (store.isLoading) {
        return <SquareLoader />
    }

    if (store.messages.length === 0) {
        return <Notice>
            <p className="no-messages">
                You currently have no messages
            </p>
        </Notice>;
    }

    return <>
        <nav>
            <Categories />

            <Search />
        </nav>

        <table className="inbox">
            <tbody>
                {store.pageItems.map((message, ix) => {
                    return <tr
                        key={ix}
                        className={message.isRead ? "read" : ""}>
                        <td>
                            <span>
                                <i className="status-indicator" />
                                <span className="category">
                                    {message.category.title}
                                </span>
                            </span>
                        </td>
                        <td
                            {...getAnalytics(message)}
                            onClick={() => viewMessage(message)}>
                            <strong title={message.content.title}>{message.content.title}</strong>
                            <small title={message.content.summary}>
                                {message.content.summary}
                            </small>
                        </td>
                        <td>
                            <time>{getMessageDate(message.date)}</time>
                        </td>
                        <td>
                            <button type="button"
                                {...getDeleteAnalytics(message)}
                                onClick={() => deleteMessageAsync(message, onDelete)}>
                                <i className="icon-imi-delete" />
                            </button>
                        </td>
                    </tr>;

                })}
            </tbody>
        </table>

        <Pager
            isLoading={false}
            currentItems={store.pageItems}
            pageCount={store.numPages}
            currentPage={store.currentPage}
            handlePageClick={(e: any) => {
                store.updateCurrentPage(e.selected);
                pushPaginationAnalytics(e.selected);
            }} />
    </>
});

export default Messages;
